import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Container, Form, InputGroup, Dropdown, OverlayTrigger, Tooltip, Modal, Spinner } from "react-bootstrap";
import {
    WorkFlowTimeLineList
} from "../services/ReduxTookit/Slice/WorkFlowRedux";
import { ProjectTermGetList } from "../services/ReduxTookit/Slice/ProjectTermRedux";
import { useDispatch, useSelector } from "react-redux";
import Loader, { LoaderType } from "./common-component/Loader";
import Skeleton from "react-loading-skeleton";
import { commonservices, TIMEZONE_LIST } from "../services/CommonServices";
import searchicon from "../assets/images/searchicon.svg";
import DatePicker from "react-datepicker";
import filtericon from "../assets/images/filter.svg";
import Nodata from "./common-component/Nodata";
import RightArrowImg from "../assets/images/right-arrow-icon.svg"
import LeftArrowImg from "../assets/images/left-arrow-icon.svg"
import editAction from "../assets/images/editaction.svg";
import { HubConnectionBuilder } from "@microsoft/signalr";
import WorkFlowMachineLogDetailModal from "./common-component/WorkFlowMachineLogDetailModal";
import WorkFlowTimeLineChart from "./common-component/WorkFlowTimeLineChart";
import FailureTrendChart from "./common-component/FailureTrendChart";
import { getWorkFlowLogDataByWorkFlowID } from "../api/apiHandler";
import homeImage from "../assets/images/home.svg";
import charticon from "../assets/images/charticon.svg";
import { useNavigate } from "react-router-dom";

// Running timer component that updates every second
const parseTimelineDate = (dateValue) => {
    if (!dateValue) return null;

    const parsedDate = dateValue instanceof Date ? new Date(dateValue) : new Date(dateValue);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatTimelineTime = (dateValue) => {
    const parsedDate = parseTimelineDate(dateValue);
    if (!parsedDate) return "";

    return `${String(parsedDate.getHours()).padStart(2, "0")}:${String(parsedDate.getMinutes()).padStart(2, "0")}:${String(parsedDate.getSeconds()).padStart(2, "0")}`;
};

function RunningTimer({ starton }) {
    const [elapsed, setElapsed] = useState(0);

    useEffect(() => {
        if (!starton) return;

        const calculateElapsed = () => {
            const now = new Date();
            const startTime = parseTimelineDate(starton);
            if (Number.isNaN(startTime.getTime())) return 0;
            return Math.max(0, Math.floor((now - startTime) / 1000));
        };

        setElapsed(calculateElapsed());

        const interval = setInterval(() => {
            setElapsed(calculateElapsed());
        }, 1000);

        return () => clearInterval(interval);
    }, [starton]);

    return (
        <div className="running-timer">
            <span style={{ fontWeight: "600", color: "#2f86ff" }}>
                {commonservices.ConvetSecondInTimeFormate(elapsed)}
            </span>
        </div>
    );
}

// Running time block that grows with time
function RunningTimeBlock({ starton, leftPx, workflowname, onClick }) {
    const [widthPx, setWidthPx] = useState(5);

    useEffect(() => {
        if (!starton) return;

        const calculateWidth = () => {
            const now = new Date();
            const startTime = parseTimelineDate(starton);
            if (Number.isNaN(startTime.getTime())) return 5;
            // Any non-zero seconds should count as the next minute (ceil minute)
            const minutes = Math.max(1, Math.ceil((now - startTime) / 60000));
            const rawWidth = minutes * 5;

            // Keep running bar right edge at/before current timeline marker.
            const nowPx = ((now.getHours() * 60) + now.getMinutes()) * 5;
            const maxWidth = Math.max(1, nowPx - leftPx);
            return Math.min(rawWidth, maxWidth);
        };

        setWidthPx(calculateWidth());

        const interval = setInterval(() => {
            setWidthPx(calculateWidth());
        }, 1000);

        return () => clearInterval(interval);
    }, [starton, leftPx]);

    return (
        <div
            style={{ width: `${widthPx}px`, left: `${leftPx}px` }}
            className="time-block start"
            title={`${workflowname} - ${formatTimelineTime(starton)} to Running`}
            onClick={onClick}
        >
            <div className="details">{workflowname}</div>
        </div>
    );
}

function WorkFlowTimeLine() {
    const PIXELS_PER_MINUTE = 5;
    const MIN_VISIBLE_BAR_PX = 5;

    const getPxFromDate = (dateValue) => {
        if (!dateValue) return 0;
        const date = parseTimelineDate(dateValue);
        if (!date) return 0;

        let minutes = date.getHours() * 60 + date.getMinutes();
        if (date.getSeconds() > 0 || date.getMilliseconds() > 0) {
            minutes += 1;
        }

        return minutes * PIXELS_PER_MINUTE;
    };

    const getWorkflowBoundsPx = (slotdata) => {
        const bounds = (slotdata || []).reduce(
            (acc, sl) => {
                const startPx = getPxFromDate(sl.starton);
                if (startPx < acc.minLeft) acc.minLeft = startPx;

                if (sl.endon) {
                    const endPx = getPxFromDate(sl.endon);
                    if (endPx > acc.maxRight) acc.maxRight = endPx;
                }

                return acc;
            },
            { minLeft: Infinity, maxRight: -Infinity }
        );

        return {
            minLeft: bounds.minLeft !== Infinity ? bounds.minLeft : 0,
            maxRight: bounds.maxRight !== -Infinity ? bounds.maxRight : null,
        };
    };

    const getBarStyle = ({
        startPx,
        endPx,
        fallbackWidthPx = MIN_VISIBLE_BAR_PX,
        minLeft = null,
        maxRight = null,
        tinyEdge = "none", // none | start | end
    }) => {
        let left = startPx;
        let right = endPx;

        if (minLeft !== null) left = Math.max(left, minLeft);
        if (maxRight !== null) right = Math.min(right, maxRight);

        if (right > left) {
            let width = Math.max(right - left, MIN_VISIBLE_BAR_PX);
            if (maxRight !== null && left + width > maxRight) {
                left = Math.max(minLeft ?? left, maxRight - width);
            }
            return { left, width };
        }

        let width = Math.max(Math.min(fallbackWidthPx, MIN_VISIBLE_BAR_PX), 1);
        if (maxRight !== null && minLeft !== null) {
            width = Math.min(width, Math.max(maxRight - minLeft, 1));
        }

        if (tinyEdge === "end" && maxRight !== null) {
            left = Math.max(minLeft ?? 0, maxRight - width);
        } else if (tinyEdge === "start" && minLeft !== null) {
            left = minLeft;
        } else {
            left = startPx;
            if (minLeft !== null) left = Math.max(left, minLeft);
            if (maxRight !== null) left = Math.min(left, maxRight - width);
        }

        return { left, width };
    };

    const [WorkflowList, setWorkflowList] = useState([]);
    const [Loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    let navigate = useNavigate();
    const slots = ["12 AM", "1 AM", "2 AM", "3 AM", "4 AM", "5 AM", "6 AM", "7 AM", "8 AM", "9 AM", "10 AM", "11 AM", "12 PM", "1 PM", "2 PM", "3 PM", "4 PM", "5 PM", "6 PM", "7 PM", "8 PM", "9 PM", "10 PM", "11 PM"];

    const [modalDetailShow, setDetailModalShow] = useState(false);
    const [workflowulogid, setworkflowulogid] = useState("");
    const [FilterData, setFilterData] = useState({ Search: "", Client: "", Status: "", Ram: "", Cpu: "" });
    const [PendingResourceFilter, setPendingResourceFilter] = useState({ Status: "", Ram: "", Cpu: "" });
    const [showResourceFilterModal, setShowResourceFilterModal] = useState(false);
    const filterButtonRef = useRef(null);
    const filterPopupRef = useRef(null);
    const workspaceExpandInitDateRef = useRef("");
    const [expandedWorkspaces, setExpandedWorkspaces] = useState({});
    const [expandedWorkflows, setExpandedWorkflows] = useState({});
    const [chartModalShow, setChartModalShow] = useState(false);
    const [chartLoading, setChartLoading] = useState(false);
    const [chartTitle, setChartTitle] = useState("Usage Chart");
    const [chartData, setChartData] = useState(null);
    const [chartsReadyCount, setChartsReadyCount] = useState(0);

    const handleChartReady = useCallback(() => {
        setChartsReadyCount(prev => prev + 1);
    }, []);

    useEffect(() => {
        if (chartsReadyCount >= 3 && chartLoading) {
            setChartLoading(false);
        }
    }, [chartsReadyCount]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (!showResourceFilterModal) return;
            if (
                filterPopupRef.current &&
                !filterPopupRef.current.contains(event.target) &&
                filterButtonRef.current &&
                !filterButtonRef.current.contains(event.target)
            ) {
                setShowResourceFilterModal(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [showResourceFilterModal]);

    useEffect(() => {
        if (showResourceFilterModal) {
            setPendingResourceFilter({
                Status: FilterData.Status,
                Ram: FilterData.Ram,
                Cpu: FilterData.Cpu,
            });
        }
    }, [showResourceFilterModal, FilterData.Status, FilterData.Ram, FilterData.Cpu]);

    const toggleWorkflowExpand = (workflowId) => {
        setExpandedWorkflows(prev => {
            // If clicking the same one, collapse it; otherwise expand only the new one
            if (prev[workflowId]) {
                return {};
            }
            return { [workflowId]: true };
        });
    };

    const toggleWorkspaceExpand = (workspaceId) => {
        setExpandedWorkspaces(prev => {
            return {
                ...prev,
                [workspaceId]: !prev[workspaceId]
            };
        });
    };

    const openWorkflowEditPage = (workflowId, e) => {
        if (e) e.stopPropagation();
        if (!workflowId) return;

        const data = commonservices.bodyEncryption(`${workflowId}?v=0&r=wl`);
        const flowPath = `/flow-new/${data}`;
        const isFlowNewPopup = document.getElementById("body")?.classList?.contains("popup-is-open");

        if (isFlowNewPopup) {
            navigate(flowPath, {
                state: {
                    isViewMode: false,
                },
            });
            return;
        }

        window.open(flowPath, "_blank", "noopener,noreferrer");
    };

    const [SelectedDate, setSelectedDate] = useState(new Date());
    const selectedDateRef = useRef(new Date());
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Update ref when SelectedDate changes
    useEffect(() => {
        selectedDateRef.current = SelectedDate;
    }, [SelectedDate]);

    const [TimeLineAPIData, setTimeLineAPIData] = useState([]);

    const [TimeLineHubConnectionID, setTimeLineHubConnectionID] = useState("");

    const getWorkFlowGetListData = useSelector(
        (item) => item?.workflowredux?.WorkFlowTimeLineListData?.data
    );

    const [WorkflowTimeLineData, setWorkflowTimeLineData] = useState({
        WorkflowsToday: 0,
        RunningNow: 0,
        Completed: 0,
        Failures: 0,
        Scheduled: 0,
        CompletedAvgTime: 0,
        Clients: []
    });
    const [FailworkflowList, setFailworkflowList] = useState([]);
    const [RamOptions, setRamOptions] = useState([]);
    const [CpuOptions, setCpuOptions] = useState([]);

    const applyTimelineFilters = useCallback((items, filters, options = { includeStatus: true }) => {
        let td = [...(items || [])];

        if (options.includeStatus && filters.Status) {
            td = td.filter((w) => (w.status || "").toUpperCase() === filters.Status.toUpperCase());
        }

        if (filters.Client) {
            td = td.filter((w) => (w.subscribername || "").toUpperCase() === filters.Client.toUpperCase());
        }

        if (filters.Search) {
            const searchVal = filters.Search.toUpperCase();
            td = td.filter((w) => {
                if (w.name && w.name.toUpperCase().indexOf(searchVal) >= 0) return true;
                if ((w.workspacename || "default").toUpperCase().indexOf(searchVal) >= 0) return true;
                if (
                    w.machinelog &&
                    typeof w.machinelog !== "string" &&
                    w.machinelog.some(
                        (m) => m.machinename && m.machinename.toUpperCase().indexOf(searchVal) >= 0
                    )
                ) {
                    return true;
                }
                return false;
            });
        }

        if (filters.Ram) {
            td = td.filter((w) =>
                w.machinelog && typeof w.machinelog !== "string"
                    ? w.machinelog.some((m) => String(m.ram) === filters.Ram)
                    : false
            );
        }

        if (filters.Cpu) {
            td = td.filter((w) =>
                w.machinelog && typeof w.machinelog !== "string"
                    ? w.machinelog.some((m) => String(m.cpu) === filters.Cpu)
                    : false
            );
        }

        return td;
    }, []);

    useEffect(() => {
        if (!WorkflowList || WorkflowList.length === 0 || !SelectedDate) return;

        const selectedDateKey = commonservices.getDateInDBFormat(SelectedDate);
        const isSameDateInit = workspaceExpandInitDateRef.current === selectedDateKey;

        setExpandedWorkspaces((prev) => {
            const hasPrevState = isSameDateInit && Object.keys(prev || {}).length > 0;

            // On first load for a selected day, keep current behavior: expand all rows.
            if (!hasPrevState) {
                const allExpanded = {};
                WorkflowList.forEach((ws) => {
                    allExpanded[ws.id] = true;
                });
                return allExpanded;
            }

            // On live updates, preserve user choices but auto-open new/running workspace rows.
            let changed = false;
            const next = { ...prev };

            WorkflowList.forEach((ws) => {
                const wsName = (ws.workspacename || "").toUpperCase();
                const hasRunningWorkflow = Array.isArray(ws.workflows)
                    && ws.workflows.some((wf) => (wf.workflowstatus || "").toUpperCase() === "START");

                if (typeof next[ws.id] === "undefined") {
                    next[ws.id] = true;
                    changed = true;
                }

                if ((wsName === "RUNNING" || hasRunningWorkflow) && next[ws.id] !== true) {
                    next[ws.id] = true;
                    changed = true;
                }
            });

            return changed ? next : prev;
        });

        workspaceExpandInitDateRef.current = selectedDateKey;
    }, [WorkflowList, SelectedDate]);

    useEffect(() => {
        dispatch(ProjectTermGetList({ category: "RAM,CPU" }))?.then((response) => {
            if (response?.payload?.isValidate) {
                if (response?.payload?.data?.data?.length > 0) {
                    setRamOptions(response.payload.data.data.filter(f => f.category === "RAM"));
                    setCpuOptions(response.payload.data.data.filter(f => f.category === "CPU"));
                }
            }
        });
    }, []);

    useEffect(() => {
        if (getWorkFlowGetListData && getWorkFlowGetListData?.isValidate) {

            let data = getWorkFlowGetListData?.data?.data.dataList1?.map((item) => ({
                ...item,
                machinelog:
                    item.machinelog && typeof item.machinelog === "string"
                        ? JSON.parse(item.machinelog)
                        : []
            }));

            let tl = document.getElementById("workflowtimeline");
            if (tl) {
                document.getElementById("workflowtimeline").setAttribute("data", JSON.stringify(data));
            }

            setTimeLineAPIData(data);

            const dataid = [
                ...new Set(
                    getWorkFlowGetListData?.data?.data.dataList1.map(item => item.dataid).filter(Boolean)
                )
            ];

            if (dataid && dataid.length > 0) {
                dataid.sort((a, b) => a - b);
                setTimeLineHubConnectionID("_" + dataid.join("_") + "_");
            }

        }
    }, [getWorkFlowGetListData]);

    useEffect(() => {

        let td = applyTimelineFilters(TimeLineAPIData, FilterData, { includeStatus: false });

        const C = [
            ...new Set(
                TimeLineAPIData.map(item => item.subscribername).filter(Boolean)
            )
        ];

        const Workspaces = [
            ...new Set(
                td.map(item => item.workspacename || "default").filter(Boolean)
            )
        ];

        const allMachineLogs = td.flatMap(item => {
            if (item.machinelog && typeof item.machinelog !== "string") return item.machinelog;
            if (item.machinelog && typeof item.machinelog === "string") {
                try { return JSON.parse(item.machinelog); } catch (e) { return []; }
            }
            return [];
        });

        const MachineNames = [...new Set(allMachineLogs.map(m => m.machinename).filter(Boolean))];

        let objAllData = {
            WorkflowsToday: td.length,
            RunningNow: td.filter((w) => w.status.toUpperCase() === "START").length,
            Completed: td.filter((w) => w.status.toUpperCase() === "END").length,
            Failures: td.filter((w) => w.status.toUpperCase() === "PARTIAL SUCCESS").length,
            Scheduled: td.filter((w) => w.status.toUpperCase() === "SCHEDULE").length,
            CompletedAvgTime: td.filter((w) => w.status.toUpperCase() === "END").reduce((sum, item) => sum + item.minutes, 0),
            Clients: C,
            Workspaces: Workspaces,
            MachineNames: MachineNames
        }

        setFailworkflowList(td.filter((w) => w.status.toUpperCase() === "PARTIAL SUCCESS"));
        setWorkflowTimeLineData(objAllData)
        MakeDataGroup();
    }, [TimeLineAPIData, FilterData, applyTimelineFilters]);

    useEffect(() => {
        if (TimeLineHubConnectionID) {
            let isDisposed = false;
            const connect = new HubConnectionBuilder()
                .withUrl(process.env.REACT_APP_SIGNAL_R, { withCredentials: false })
                .withAutomaticReconnect()
                .build();

            const onTimelineUpdate = (data) => {
                // Skip SignalR updates if viewing a past date
                const now = new Date();
                const sel = selectedDateRef.current;
                if (sel && (sel.getFullYear() !== now.getFullYear() || sel.getMonth() !== now.getMonth() || sel.getDate() !== now.getDate())) {
                    return;
                }

                let tl = document.getElementById("workflowtimeline");
                if (tl) {
                    let _TimeLineAPIData = structuredClone(TimeLineAPIData);
                    let tldata = tl.getAttribute("data");
                    if (tldata) {
                        _TimeLineAPIData = JSON.parse(tldata);
                    }
                    let chkW = _TimeLineAPIData.filter((w) => w.workflowulogid === data.data.workflowulogid);

                    if (chkW && chkW.length > 0) {
                        chkW[0].minutes = Math.round(commonservices.GetSecondCounts(chkW[0].starton, chkW[0].endon) / 60);
                        if (chkW[0].minutes < 1) {
                            chkW[0].minutes = 1;
                        }
                        if (data.datatype === "tra_workflowschedulelog") {
                            const prevStatus = (chkW[0].status || "").toUpperCase();
                            chkW[0].status = data.data.status;
                            if (data.data.status && data.data.status.toUpperCase() === "START") {
                                // Keep earliest start time for a currently running workflow to avoid bar reset flicker.
                                const existingStart = chkW[0].starton ? new Date(chkW[0].starton) : null;
                                const incomingStart = data.data.starton ? new Date(data.data.starton) : null;

                                const existingMs = existingStart && !Number.isNaN(existingStart.getTime()) ? existingStart.getTime() : null;
                                const incomingMs = incomingStart && !Number.isNaN(incomingStart.getTime()) ? incomingStart.getTime() : null;

                                if (prevStatus === "START" && existingMs !== null) {
                                    chkW[0].starton = existingStart;
                                } else if (existingMs !== null && incomingMs !== null) {
                                    chkW[0].starton = new Date(Math.min(existingMs, incomingMs));
                                } else if (existingMs !== null) {
                                    chkW[0].starton = existingStart;
                                } else if (incomingMs !== null) {
                                    chkW[0].starton = incomingStart;
                                } else {
                                    chkW[0].starton = new Date();
                                }

                                chkW[0].endon = new Date();
                                chkW[0].minutes = 1;
                            }
                            if (data.data.status.toUpperCase() !== "START") {

                                if (SelectedDate) {
                                    let body = {
                                        date: commonservices.getDateInDBFormat(SelectedDate),
                                        timezone: browserTimezone,
                                    };
                                    dispatch(WorkFlowTimeLineList(body));
                                }
                            }
                        }
                    }
                    else {
                        let fW = _TimeLineAPIData.filter((w) => w.dataid === data.id);
                        if (fW && fW.length > 0) {
                            let _fW = structuredClone(fW[0]);
                            _fW.starton = data.data.starton ? new Date(data.data.starton) : new Date();
                            _fW.endon = new Date();
                            _fW.minutes = 1;
                            _fW.status = "start";
                            _fW.workflowulogid = data.data.workflowulogid;
                            _fW.machinelog = [];
                            _fW.noofmachinerun = 0;
                            _TimeLineAPIData.unshift(_fW);

                        }
                    }

                    if (data.datatype === "tra_workflowschedulemachinelog") {
                        let chkWFM = _TimeLineAPIData.filter((w) => w.workflowulogid === data.data.workflowulogid);
                        if (chkWFM && chkWFM.length > 0) {
                            let _fWM = chkWFM[0].machinelog.filter((m) => m.machineulogid === data.data.machineulogid)
                            if (_fWM && _fWM.length > 0) {
                                _fWM[0].endon = new Date();
                                _fWM[0].status = data.data.status;
                            }
                            else {
                                let m = {
                                    "machineulogid": data.data.machineulogid,
                                    "machinename": data.data.assemblyname.split("-")[0],
                                    "workflowmachinename": data.data.workflowmachinename,
                                    "starton": new Date(),
                                    "endon": new Date(),
                                    "status": data.data.status
                                }
                                chkWFM[0].machinelog.unshift(m);
                                chkWFM[0].noofmachinerun = chkWFM[0].noofmachinerun + 1;

                                if (chkWFM[0].noofmachine < chkWFM[0].noofmachinerun) {
                                    chkWFM[0].noofmachine = chkWFM[0].noofmachinerun;
                                }
                            }
                        }
                    }

                    tl.setAttribute("data", JSON.stringify(_TimeLineAPIData));

                    setTimeLineAPIData(_TimeLineAPIData);
                }
            };

            connect.on("gettimelineupdate", onTimelineUpdate);

            connect.onreconnected(() => {
                if (!isDisposed) {
                    connect.send("timelineconnect", TimeLineHubConnectionID).catch(() => { });
                }
            });

            connect.start()
                .then(() => {
                    if (!isDisposed) {
                        connect.send("timelineconnect", TimeLineHubConnectionID).catch(() => { });
                    }
                })
                .catch(() => { });

            return () => {
                isDisposed = true;
                connect.off("gettimelineupdate", onTimelineUpdate);
                connect.stop().catch(() => { });
            };
        }
    }, [TimeLineHubConnectionID]);

    useEffect(() => {
        // Debug helper: simulate SignalR payload from browser console.
        window.__WF_SIMULATE_TIMELINE_UPDATE = (payload) => {
            if (!payload || !payload.data) return;

            let tl = document.getElementById("workflowtimeline");
            if (!tl) return;

            let _TimeLineAPIData = structuredClone(TimeLineAPIData || []);
            const tldata = tl.getAttribute("data");
            if (tldata) {
                try {
                    _TimeLineAPIData = JSON.parse(tldata);
                } catch {
                    _TimeLineAPIData = structuredClone(TimeLineAPIData || []);
                }
            }

            let chkW = _TimeLineAPIData.filter((w) => w.workflowulogid === payload.data.workflowulogid);

            if (chkW && chkW.length > 0) {
                chkW[0].minutes = Math.round(commonservices.GetSecondCounts(chkW[0].starton, chkW[0].endon) / 60);
                if (chkW[0].minutes < 1) chkW[0].minutes = 1;

                if (payload.datatype === "tra_workflowschedulelog") {
                    const prevStatus = (chkW[0].status || "").toUpperCase();
                    chkW[0].status = payload.data.status;

                    if (payload.data.status && payload.data.status.toUpperCase() === "START") {
                        const existingStart = chkW[0].starton ? new Date(chkW[0].starton) : null;
                        const incomingStart = payload.data.starton ? new Date(payload.data.starton) : null;

                        const existingMs = existingStart && !Number.isNaN(existingStart.getTime()) ? existingStart.getTime() : null;
                        const incomingMs = incomingStart && !Number.isNaN(incomingStart.getTime()) ? incomingStart.getTime() : null;

                        if (prevStatus === "START" && existingMs !== null) {
                            chkW[0].starton = existingStart;
                        } else if (existingMs !== null && incomingMs !== null) {
                            chkW[0].starton = new Date(Math.min(existingMs, incomingMs));
                        } else if (existingMs !== null) {
                            chkW[0].starton = existingStart;
                        } else if (incomingMs !== null) {
                            chkW[0].starton = incomingStart;
                        } else {
                            chkW[0].starton = new Date();
                        }

                        chkW[0].endon = new Date();
                        chkW[0].minutes = 1;
                    }

                    if (payload.data.status && payload.data.status.toUpperCase() !== "START") {
                        const sel = selectedDateRef.current;
                        if (sel) {
                            let body = {
                                date: commonservices.getDateInDBFormat(sel),
                                timezone: browserTimezone,
                            };
                            dispatch(WorkFlowTimeLineList(body));
                        }
                    }
                }
            } else {
                let fW = _TimeLineAPIData.filter((w) => w.dataid === payload.id);
                if (fW && fW.length > 0) {
                    let _fW = structuredClone(fW[0]);
                    _fW.starton = payload.data.starton ? new Date(payload.data.starton) : new Date();
                    _fW.endon = new Date();
                    _fW.minutes = 1;
                    _fW.status = "start";
                    _fW.workflowulogid = payload.data.workflowulogid;
                    _fW.machinelog = [];
                    _fW.noofmachinerun = 0;
                    _TimeLineAPIData.unshift(_fW);
                }
            }

            if (payload.datatype === "tra_workflowschedulemachinelog") {
                let chkWFM = _TimeLineAPIData.filter((w) => w.workflowulogid === payload.data.workflowulogid);
                if (chkWFM && chkWFM.length > 0) {
                    let _fWM = chkWFM[0].machinelog.filter((m) => m.machineulogid === payload.data.machineulogid);
                    if (_fWM && _fWM.length > 0) {
                        _fWM[0].endon = new Date();
                        _fWM[0].status = payload.data.status;
                    } else {
                        let m = {
                            "machineulogid": payload.data.machineulogid,
                            "machinename": (payload.data.assemblyname || "").split("-")[0],
                            "workflowmachinename": payload.data.workflowmachinename,
                            "starton": new Date(),
                            "endon": new Date(),
                            "status": payload.data.status
                        };
                        chkWFM[0].machinelog.unshift(m);
                        chkWFM[0].noofmachinerun = chkWFM[0].noofmachinerun + 1;

                        if (chkWFM[0].noofmachine < chkWFM[0].noofmachinerun) {
                            chkWFM[0].noofmachine = chkWFM[0].noofmachinerun;
                        }
                    }
                }
            }

            tl.setAttribute("data", JSON.stringify(_TimeLineAPIData));
            setTimeLineAPIData(_TimeLineAPIData);
        };

        return () => {
            delete window.__WF_SIMULATE_TIMELINE_UPDATE;
        };
    }, [TimeLineAPIData, dispatch]);

    useEffect(() => {
        MakeDataGroup();
    }, [FilterData]);

    function fnWorkflowMachineLoggetlist(_workflowulogid) {
        setworkflowulogid(_workflowulogid);
        setDetailModalShow(true);
    }

    function getChartDataFromApiResponse(res) {
        const chartList =
            res?.data?.dataList1 ||
            res?.data?.DataList1 ||
            res?.data?.datalist1 ||
            [];

        if (!chartList || chartList.length === 0) return null;

        const rawChartData = chartList[0]?.chartdata;
        if (!rawChartData) return null;

        if (typeof rawChartData === "string") {
            try {
                return JSON.parse(rawChartData);
            } catch {
                return null;
            }
        }

        return rawChartData;
    }

    async function openChartModal(workflowulogid, machineid, title) {
        if (!workflowulogid) return;

        setChartTitle(title || "Usage Chart");
        setChartData(null);
        setChartsReadyCount(0);
        setChartModalShow(true);
        setChartLoading(true);

        try {
            const res = await getWorkFlowLogDataByWorkFlowID({
                workflowulogid,
                machineid,
            });

            if (res?.status_code === 1) {
                setChartData(getChartDataFromApiResponse(res));
            } else {
                setChartData(null);
            }
        } catch (error) {
            setChartData(null);
        } finally {
            // chartLoading will be turned off by chartsReadyCount effect
        }
    }

    function MakeDataGroup() {
        let data = []
        if (TimeLineAPIData) {
            let td = applyTimelineFilters(TimeLineAPIData, FilterData, { includeStatus: true });
            const RUNNING_WORKSPACE_NAME = "Running";
            const getSafeDurationSeconds = (slot) => {
                if (!slot) return 0;

                const diffSeconds = commonservices.GetSecondCounts(slot.starton, slot.endon);
                if (Number.isFinite(diffSeconds) && diffSeconds > 0) {
                    return diffSeconds;
                }

                const minutes = Number(slot.minutes);
                if (Number.isFinite(minutes) && minutes > 0) {
                    return Math.round(minutes * 60);
                }

                return 0;
            };

            if (td && td.length > 0) {
                // Move currently running workflows to a single synthetic workspace: "Running".
                // When a workflow is no longer START, it naturally returns to its original workspace.
                const latestByWorkflow = {};
                const activeRunningByWorkflow = {};
                td.forEach((item) => {
                    const workflowKey = String(item.dataid ?? item.workflowid ?? item.name ?? "");
                    const itemMs = new Date(item.starton || item.endon || 0).getTime();
                    const safeMs = Number.isNaN(itemMs) ? 0 : itemMs;

                    const itemStatus = (item.status || "").toUpperCase();
                    const hasWorkflowRunId = Boolean(item.workflowulogid);
                    if (itemStatus === "START" && hasWorkflowRunId) {
                        activeRunningByWorkflow[workflowKey] = true;
                    }

                    if (!latestByWorkflow[workflowKey] || safeMs >= latestByWorkflow[workflowKey].ms) {
                        latestByWorkflow[workflowKey] = {
                            ms: safeMs,
                            status: itemStatus,
                        };
                    }
                });

                const runningWorkflowKeys = new Set(
                    Object.entries(latestByWorkflow)
                        .filter(([k, v]) => activeRunningByWorkflow[k] || v.status === "START")
                        .map(([k]) => k)
                );

                const tdWithDisplayWorkspace = td.map((item) => {
                    const workflowKey = String(item.dataid ?? item.workflowid ?? item.name ?? "");
                    return {
                        ...item,
                        _displayWorkspaceName: runningWorkflowKeys.has(workflowKey)
                            ? RUNNING_WORKSPACE_NAME
                            : (item.workspacename || "default"),
                    };
                });

                // Group by workspace, and for non-running workspaces split by client.
                const workspaceEntries = [
                    ...new Map(
                        tdWithDisplayWorkspace.map((item) => {
                            const wsName = item._displayWorkspaceName || "default";
                            const clientName = item.subscribername || "";
                            const groupKey = wsName === RUNNING_WORKSPACE_NAME
                                ? RUNNING_WORKSPACE_NAME
                                : `${wsName}__${clientName}`;
                            return [groupKey, { wsName, clientName }];
                        })
                    ).values()
                ];

                const today = new Date();
                const isToday = SelectedDate && SelectedDate.getFullYear() === today.getFullYear() && SelectedDate.getMonth() === today.getMonth() && SelectedDate.getDate() === today.getDate();

                for (let IWS = 0; IWS < workspaceEntries.length; IWS++) {
                    const wsEntry = workspaceEntries[IWS];
                    let wsName = wsEntry.wsName;
                    let wsClientName = wsEntry.clientName;
                    let wsTd = tdWithDisplayWorkspace.filter((item) => {
                        const itemWs = item._displayWorkspaceName || "default";
                        if (itemWs !== wsName) return false;
                        if (wsName === RUNNING_WORKSPACE_NAME) return true;
                        return (item.subscribername || "") === wsClientName;
                    });
                    const wsRowId = `ws-${String(wsName).replace(/\s+/g, "_")}-${String(wsClientName || "all").replace(/\s+/g, "_")}`;

                    let wsGroup = {
                        id: wsRowId,
                        workspacename: wsName,
                        clientname: wsClientName,
                        totaltime: 0,
                        workflows: []
                    };

                    const W = [
                        ...new Set(
                            wsTd.map(item => item.name).filter(Boolean)
                        )
                    ];

                    for (let IW = 0; IW < W.length; IW++) {
                        const workflowName = W[IW];
                        const wfRowId = `wf-${wsRowId}-${String(workflowName).replace(/\s+/g, "_")}`;
                        let objW = {
                            id: wfRowId,
                            workflowid: 0,
                            workflowname: workflowName,
                            workflowstatus: "",
                            workflowstarton: "",
                            workflowendon: "",
                            workflowtime: "",

                            workspacename: "",
                            subscribername: "",

                            machinestatus: "",
                            machinestarton: "",
                            noofmachine: 0,
                            noofmachinerun: 0,
                            slotdata: []
                        }

                        let slotdata = wsTd.filter((item) => item.name === workflowName);
                        if (slotdata && slotdata.length > 0) {
                            // Ensure deterministic order even when live SignalR mutates list order.
                            const sortedSlotData = [...slotdata].sort((a, b) => {
                                const aTime = new Date(a.starton || a.endon || 0).getTime();
                                const bTime = new Date(b.starton || b.endon || 0).getTime();
                                return aTime - bTime;
                            });

                            objW.slotdata = sortedSlotData;

                            // Summary selection priority:
                            // 1) active START run (latest by time)
                            // 2) latest non-schedule run
                            // 3) latest run (fallback, including schedule-only workflows)
                            let latestRun = sortedSlotData[sortedSlotData.length - 1];
                            let runningRuns = sortedSlotData.filter((s) => (s.status || "").toUpperCase() === "START");
                            if (runningRuns.length > 0) {
                                latestRun = runningRuns[runningRuns.length - 1];
                            } else {
                                let nonScheduleRuns = sortedSlotData.filter((s) => (s.status || "").toUpperCase() !== "SCHEDULE");
                                if (nonScheduleRuns.length > 0) {
                                    latestRun = nonScheduleRuns[nonScheduleRuns.length - 1];
                                }
                            }

                            objW.workflowid = latestRun.dataid;
                            if (latestRun.dataid) {
                                objW.id = `wf-${wsRowId}-${latestRun.dataid}`;
                            }
                            objW.workflowstatus = latestRun.status;
                            objW.workspacename = latestRun.workspacename;
                            objW.subscribername = latestRun.subscribername;
                            objW.noofmachine = latestRun.noofmachine;
                            objW.noofmachinerun = latestRun.noofmachinerun;

                            objW.workflowstarton = latestRun.starton;
                            objW.workflowendon = latestRun.endon;

                            objW.workflowtime = "";

                            if (objW.workflowstatus.toUpperCase() === "END") {
                                objW.machinestatus = "All completed";
                                const safeSeconds = getSafeDurationSeconds(latestRun);
                                objW.workflowtime = safeSeconds > 0 ? commonservices.ConvetSecondInTimeFormate(safeSeconds) : "";
                            }
                            else if (objW.workflowstatus.toUpperCase() === "PARTIAL SUCCESS") {
                                objW.machinestatus = "";
                                if (latestRun.machinelog && latestRun.machinelog.length > 0) {
                                    let F = latestRun.machinelog.filter((m) => m.status.toUpperCase() === "FAILED");
                                    if (F && F.length > 0) {
                                        objW.machinestatus = F[0].machinename
                                    }
                                }
                                const safeSeconds = getSafeDurationSeconds(latestRun);
                                objW.workflowtime = safeSeconds > 0 ? commonservices.ConvetSecondInTimeFormate(safeSeconds) : "";
                            }
                            else if (objW.workflowstatus.toUpperCase() === "START") {
                                objW.machinestatus = "Waiting for next machine";
                                if (latestRun.machinelog && latestRun.machinelog.length > 0) {
                                    let F = latestRun.machinelog.filter((m) => m.status.toUpperCase() === "START");
                                    if (F && F.length > 0) {
                                        objW.machinestatus = F[0].machinename;
                                        objW.machinestarton = F[0].starton;
                                    }
                                }
                            }
                            else if (objW.workflowstatus.toUpperCase() === "SCHEDULE") {
                                objW.machinestatus = "Pending";
                                objW.workflowtime = "";
                            }
                        }
                        wsGroup.workflows.push(objW);
                    }

                    // Calculate total seconds from ALL completed/failed runs across all workflows
                    let totalSeconds = 0;
                    wsGroup.workflows.forEach((wf) => {
                        if (wf.slotdata && wf.slotdata.length > 0) {
                            wf.slotdata.forEach((slot) => {
                                const status = (slot.status || "").toUpperCase();
                                if (wsGroup.workspacename === RUNNING_WORKSPACE_NAME) {
                                    if (status === "START" && slot.starton) {
                                        const secs = commonservices.GetSecondCounts(slot.starton, new Date().toISOString());
                                        if (secs > 0) totalSeconds += secs;
                                    }
                                    return;
                                }

                                if ((status === "END" || status === "PARTIAL SUCCESS") && slot.starton && slot.endon) {
                                    let secs = commonservices.GetSecondCounts(slot.starton, slot.endon);
                                    if (secs > 0) {
                                        totalSeconds += secs;
                                    }
                                }
                            });
                        }
                    });

                    // Workspace-wise workflow sorting:
                    // 1) In synthetic "Running" workspace: latest running workflow first.
                    // 2) In other workspaces: latest run first, then scheduled workflows by scheduled time desc.
                    wsGroup.workflows.sort((a, b) => {
                        const getLatestMsForStatus = (wf, status) => {
                            let latest = 0;
                            if (wf.slotdata && wf.slotdata.length > 0) {
                                wf.slotdata.forEach((slot) => {
                                    if ((slot.status || "").toUpperCase() !== status) return;
                                    const t = new Date(slot.starton || slot.endon || 0).getTime();
                                    if (!Number.isNaN(t) && t > latest) latest = t;
                                });
                            }
                            return latest;
                        };

                        const getEarliestMsForStatus = (wf, status) => {
                            let earliest = null;
                            if (wf.slotdata && wf.slotdata.length > 0) {
                                wf.slotdata.forEach((slot) => {
                                    if ((slot.status || "").toUpperCase() !== status) return;
                                    const t = new Date(slot.starton || slot.endon || 0).getTime();
                                    if (Number.isNaN(t) || t <= 0) return;
                                    if (earliest === null || t < earliest) earliest = t;
                                });
                            }
                            return earliest === null ? Number.MAX_SAFE_INTEGER : earliest;
                        };

                        const getLatestMsForNonSchedule = (wf) => {
                            let latest = 0;
                            if (wf.slotdata && wf.slotdata.length > 0) {
                                wf.slotdata.forEach((slot) => {
                                    if ((slot.status || "").toUpperCase() === "SCHEDULE") return;
                                    const t = new Date(slot.starton || slot.endon || 0).getTime();
                                    if (!Number.isNaN(t) && t > latest) latest = t;
                                });
                            }
                            if (!latest && wf.workflowstarton) {
                                const t = new Date(wf.workflowstarton).getTime();
                                if (!Number.isNaN(t)) latest = t;
                            }
                            return latest;
                        };

                        if (wsGroup.workspacename === RUNNING_WORKSPACE_NAME) {
                            const aRun = getLatestMsForStatus(a, "START");
                            const bRun = getLatestMsForStatus(b, "START");
                            if (bRun !== aRun) return bRun - aRun;
                            return (a.workflowname || "").localeCompare(b.workflowname || "");
                        }

                        const aIsSchedule = (a.workflowstatus || "").toUpperCase() === "SCHEDULE";
                        const bIsSchedule = (b.workflowstatus || "").toUpperCase() === "SCHEDULE";
                        if (aIsSchedule !== bIsSchedule) return aIsSchedule ? 1 : -1;

                        if (!aIsSchedule && !bIsSchedule) {
                            const aLatestRun = getLatestMsForNonSchedule(a);
                            const bLatestRun = getLatestMsForNonSchedule(b);
                            if (bLatestRun !== aLatestRun) return bLatestRun - aLatestRun;
                        } else {
                            // For scheduled workflows, show the nearest upcoming/earliest schedule first.
                            const aEarliestSchedule = getEarliestMsForStatus(a, "SCHEDULE");
                            const bEarliestSchedule = getEarliestMsForStatus(b, "SCHEDULE");
                            if (aEarliestSchedule !== bEarliestSchedule) return aEarliestSchedule - bEarliestSchedule;
                        }

                        return (a.workflowname || "").localeCompare(b.workflowname || "");
                    });

                    wsGroup.totaltime = totalSeconds > 0 ? commonservices.ConvetSecondInTimeFormate(totalSeconds) : "";
                    data.push(wsGroup);
                }

                // Keep synthetic "Running" workspace at top; other workspaces stay alphabetically stable.
                data.sort((a, b) => {
                    const aIsRunningWs = (a.workspacename || "") === RUNNING_WORKSPACE_NAME;
                    const bIsRunningWs = (b.workspacename || "") === RUNNING_WORKSPACE_NAME;
                    if (aIsRunningWs !== bIsRunningWs) return aIsRunningWs ? -1 : 1;
                    return (a.workspacename || "").localeCompare(b.workspacename || "");
                });
            }
        }
        setWorkflowList(data);
        setLoading(false);
    }

    useEffect(() => {
        if (SelectedDate) {
            setLoading(true);
            let body = {
                date: commonservices.getDateInDBFormat(SelectedDate),
                timezone: browserTimezone,
            };
            dispatch(WorkFlowTimeLineList(body));


            let MoveToCurrentTime = setInterval(function () {
                clearInterval(MoveToCurrentTime);
                var _d = new Date();
                let crrenttimeid = commonservices.convertTimeToLocal12ForAsID(_d)
                if (crrenttimeid) {
                    if (document.getElementById(crrenttimeid)) {
                        document.getElementById(crrenttimeid).scrollIntoView({
                            behavior: "smooth",
                            inline: "center",
                            block: "nearest",
                        });
                    }
                }
            }, 500);
        }
    }, [SelectedDate]);

    useEffect(() => {
        const updateNowLine = () => {
            let nowLine = document.getElementById("nowLine");
            if (!nowLine) return;

            var _d = new Date();
            const selected = selectedDateRef.current;
            const isToday = selected &&
                selected.getFullYear() === _d.getFullYear() &&
                selected.getMonth() === _d.getMonth() &&
                selected.getDate() === _d.getDate();

            if (isToday) {
                nowLine.setAttribute("data-time", commonservices.convertTimeToLocal12Formate(_d));
                nowLine.style.left = ((((_d.getHours() * 60) + _d.getMinutes()) * 5) + 700) + "px";
                nowLine.style.display = "block";

                let table = document.getElementById("workflowtimeline");
                if (table) {
                    let height = table.scrollHeight;
                    nowLine.style.height = height + "px";
                }
            } else {
                nowLine.style.display = "none";
            }
        };

        updateNowLine();
        const nowLineInterval = setInterval(updateNowLine, 1000);
        return () => clearInterval(nowLineInterval);
    }, []);

    const handleChange = (e) => {
        setSelectedDate(e);
    };

    const isRecordOnSelectedDate = (dateValue) => {
        if (!SelectedDate || !dateValue) return false;

        const selected = new Date(SelectedDate);
        const selectedY = selected.getFullYear();
        const selectedM = selected.getMonth();
        const selectedD = selected.getDate();

        // const formattedLocalDate = moment(dateValue).format("MM/DD/yyyy");

        // if (formattedLocalDate) {
        //     const formattedDate = new Date(formattedLocalDate);
        //     if (!Number.isNaN(formattedDate.getTime())) {
        //         return (
        //             formattedDate.getFullYear() === selectedY &&
        //             formattedDate.getMonth() === selectedM &&
        //             formattedDate.getDate() === selectedD
        //         );
        //     }
        // }

        // const fallbackDateValue = String(dateValue).endsWith("Z") ? dateValue : `${dateValue}Z`;
        const fallbackDate = new Date(dateValue);
        if (Number.isNaN(fallbackDate.getTime())) return false;

        return (
            fallbackDate.getFullYear() === selectedY &&
            fallbackDate.getMonth() === selectedM &&
            fallbackDate.getDate() === selectedD
        );
    };

    const now = new Date();
    const isSelectedDateToday = SelectedDate &&
        SelectedDate.getFullYear() === now.getFullYear() &&
        SelectedDate.getMonth() === now.getMonth() &&
        SelectedDate.getDate() === now.getDate();

    const getFailureChartData = (rows) => {
        const hours = Array.from(
            { length: 24 },
            (_, i) => ({
                hour: i,
                count: 0
            })
        );

        rows
            .forEach(x => {

                const hour =
                    new Date(x.starton).getHours();

                hours[hour].count++;

            });

        return hours;
    };

    const failureChartData = useMemo(() => {

        return getFailureChartData(FailworkflowList);

    }, [FailworkflowList]);

    return (
        <>
            <div className="workflowtimeline-page-main all-page-top">
                <Container>
                    <div className="top-bot-section flex-column align-items-start gap-3">
                        <h6>
                            <img
                                src={homeImage}
                                alt="home"
                                title="home"
                                style={{ cursor: "pointer", marginBottom: "3px" }}
                                onClick={() => navigate("/")}
                            />
                            <span style={{ fontWeight: "400", color: "#666666" }}>{" | "}</span>
                            <span className="mt-1">
                                {/* Workflow {workflowList?.length > 0  && `(${workflowList?.length})`} */}
                                Control Center
                            </span>
                        </h6>
                    </div>
                </Container>
                <div className="main-page-content">
                    <Container>
                        <div className="workflowtimeline mt-3 mb-3">
                            <section className="grid cols-5">
                                <div className={`card cursor-pointer ${FilterData.Status === "" ? "active" : ""}`}
                                    onClick={(e) => setFilterData(prev => ({ ...prev, Status: "" }))}
                                >
                                    <div className="hd"><div className="title"><h3>Workflows Today</h3><p>All runs: completed + running + scheduled</p></div></div>
                                    <div className="metric">
                                        <div><div className="big">{Loading ? <Skeleton width={50} height={28} /> : WorkflowTimeLineData.WorkflowsToday}</div><div className="sub">{Loading ? <Skeleton width={80} height={12} /> : `Across ${WorkflowTimeLineData.Clients.length} clients`}</div></div>
                                        <div className="spark" aria-hidden="true">
                                            <svg width="92" height="28" viewBox="0 0 92 28" fill="none">
                                                <path d="M2 22c10-8 15 1 25-9s16 10 28-2 14-3 35-9" stroke="rgba(47,134,255,.9)" strokeWidth="2.2" strokeLinecap="round" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                <div className={`card cursor-pointer ${FilterData.Status === "START" ? "active" : ""}`}
                                    onClick={(e) => setFilterData(prev => ({ ...prev, Status: "START" }))}
                                >
                                    <div className="hd"><div className="title"><h3>Running Now</h3><p>Live executions</p></div></div>
                                    <div className="metric">
                                        <div><div className="big">{Loading ? <Skeleton width={50} height={28} /> : WorkflowTimeLineData.RunningNow}</div><div className="sub">{Loading ? <Skeleton width={70} height={12} /> : "On schedule"}</div></div>
                                        <div className="spark" aria-hidden="true">
                                            <svg width="92" height="28" viewBox="0 0 92 28" fill="none">
                                                <path d="M2 16c12 3 18-10 28-9 10 1 12 15 24 14 12-1 18-9 36-6" stroke="rgba(74,163,255,.95)" strokeWidth="2.2" strokeLinecap="round" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                <div className={`card cursor-pointer ${FilterData.Status === "END" ? "active" : ""}`}
                                    onClick={(e) => setFilterData(prev => ({ ...prev, Status: "END" }))}
                                >
                                    <div className="hd"><div className="title"><h3>Completed</h3><p>Successful today</p></div></div>
                                    <div className="metric">
                                        <div><div className="big">{Loading ? <Skeleton width={50} height={28} /> : WorkflowTimeLineData.Completed}</div><div className="sub">{Loading ? <Skeleton width={100} height={12} /> : `Avg runtime ${commonservices.calculateDurationSec(Math.round((WorkflowTimeLineData.CompletedAvgTime / WorkflowTimeLineData.Completed) * 60))}`}</div></div>
                                        <div className="spark" aria-hidden="true">
                                            <svg width="92" height="28" viewBox="0 0 92 28" fill="none">
                                                <path d="M2 20c10-2 18-8 28-4 10 4 12 8 24 3 12-5 18-12 36-13" stroke="rgba(29,191,115,.92)" strokeWidth="2.2" strokeLinecap="round" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                <div className={`card cursor-pointer ${FilterData.Status === "PARTIAL SUCCESS" ? "active" : ""}`}
                                    onClick={(e) => setFilterData(prev => ({ ...prev, Status: "PARTIAL SUCCESS" }))}
                                >
                                    <div className="hd"><div className="title"><h3>Failures</h3><p>Need attention</p></div></div>
                                    <div className="metric">
                                        <div><div className="big">{Loading ? <Skeleton width={50} height={28} /> : WorkflowTimeLineData.Failures}</div><div className="sub">{Loading ? <Skeleton width={80} height={12} /> : "Click to review"}</div></div>
                                        {/* <div className="spark" aria-hidden="true">
                                            <svg width="92" height="28" viewBox="0 0 92 28" fill="none">
                                                <path d="M2 8c12 6 18 2 28 6 10 4 12 9 24 10 12 1 18-6 36 0" stroke="rgba(255,77,79,.92)" strokeWidth="2.2" strokeLinecap="round" />
                                            </svg>
                                        </div> */}
                                        <div className="spark">

                                            <FailureTrendChart
                                                data={failureChartData}
                                            />

                                        </div>
                                    </div>
                                </div>

                                <div className={`card cursor-pointer ${FilterData.Status === "SCHEDULE" ? "active" : ""}`}
                                    onClick={(e) => setFilterData(prev => ({ ...prev, Status: "SCHEDULE" }))}
                                >
                                    <div className="hd"><div className="title"><h3>Scheduled</h3><p>Pending executions</p></div></div>
                                    <div className="metric">
                                        <div><div className="big">{Loading ? <Skeleton width={50} height={28} /> : WorkflowTimeLineData.Scheduled}</div><div className="sub">{Loading ? <Skeleton width={80} height={12} /> : "Click to review"}</div></div>
                                        <div className="spark" aria-hidden="true">
                                            <svg width="92" height="28" viewBox="0 0 92 28" fill="none">
                                                <path d="M2 22c10-8 15 1 25-9s16 10 28-2 14-3 35-9" stroke="rgba(148,163,184,.9)" strokeWidth="2.2" strokeLinecap="round" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            <section className="card gantt-card mt-3">
                                <div className="toolbar">
                                    <div className="lefttools">
                                        <div className="input" role="search">
                                            <svg className="ic2" viewBox="0 0 24 24" fill="none">
                                                <path d="M10.5 18.5a8 8 0 1 1 0-16 8 8 0 0 1 0 16Z" stroke="rgba(15,37,58,.85)" stroke-width="1.7"></path>
                                                <path d="M16.7 16.7 21 21" stroke="rgba(15,37,58,.85)" stroke-width="1.7" stroke-linecap="round"></path>
                                            </svg>
                                            <input placeholder="Search workflow, workspace, machine" value={FilterData.Search}
                                                onChange={(e) => setFilterData(prev => ({
                                                    ...prev,
                                                    Search: e.target.value
                                                }))} />
                                        </div>

                                        <select className="select" aria-label="Client filter" value={FilterData.Client} onChange={(e) => setFilterData(prev => ({
                                            ...prev,
                                            Client: e.target.value
                                        }))} >
                                            <option value="">All Clients</option>
                                            {
                                                WorkflowTimeLineData.Clients.map((c) => <option value={c}>{c}</option>)
                                            }
                                        </select>

                                        <div className="workflowtimeline-filter-anchor" ref={filterButtonRef}>
                                            <button
                                                type="button"
                                                id="sort-filter"
                                                className="sort-filter-trigger"
                                                title="Filter"
                                                onClick={() => setShowResourceFilterModal((prev) => !prev)}
                                            >
                                                <img src={filtericon} alt="Filter" />
                                                <span>Filter</span>
                                            </button>

                                            {showResourceFilterModal && (
                                                <div ref={filterPopupRef} className="schedular-popup sort-and-filter-popup workflow-filter-box workflowtimeline-filter-popup">
                                                    <div className="popup-title d-block p-0">
                                                        <div className="workflowtimeline-filter-tabs">
                                                            <button type="button" className="active">Filter</button>
                                                            <button type="button" className="workflowtimeline-filter-close" onClick={() => setShowResourceFilterModal(false)}>x</button>
                                                        </div>
                                                    </div>
                                                    <div className="popup-body">
                                                        <div className="single-filter">
                                                            <div className="d-flex align-items-center justify-content-between common-tab-titles">
                                                                <h5>Status</h5>
                                                            </div>
                                                            <select
                                                                className="common-input"
                                                                value={PendingResourceFilter.Status}
                                                                onChange={(e) => setPendingResourceFilter(prev => ({
                                                                    ...prev,
                                                                    Status: e.target.value
                                                                }))}
                                                            >
                                                                <option value="">All Status</option>
                                                                <option value="START">Running</option>
                                                                <option value="END">Completed</option>
                                                                <option value="PARTIAL SUCCESS">Failed</option>
                                                                <option value="SCHEDULE">Scheduled</option>
                                                            </select>
                                                        </div>
                                                        <div className="single-filter">
                                                            <div className="d-flex align-items-center justify-content-between common-tab-titles">
                                                                <h5>RAM</h5>
                                                            </div>
                                                            <select
                                                                className="common-input"
                                                                value={PendingResourceFilter.Ram}
                                                                onChange={(e) => setPendingResourceFilter(prev => ({
                                                                    ...prev,
                                                                    Ram: e.target.value
                                                                }))}
                                                            >
                                                                <option value="">All RAM</option>
                                                                {RamOptions.map((r) => <option key={r.term} value={r.term}>{r.term}</option>)}
                                                            </select>
                                                        </div>
                                                        <div className="single-filter">
                                                            <div className="d-flex align-items-center justify-content-between common-tab-titles">
                                                                <h5>CPU</h5>
                                                            </div>
                                                            <select
                                                                className="common-input"
                                                                value={PendingResourceFilter.Cpu}
                                                                onChange={(e) => setPendingResourceFilter(prev => ({
                                                                    ...prev,
                                                                    Cpu: e.target.value
                                                                }))}
                                                            >
                                                                <option value="">All CPU</option>
                                                                {CpuOptions.map((c) => <option key={c.term} value={c.term}>{c.term}</option>)}
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div className="popup-footer">
                                                        <button
                                                            className="grey-btn"
                                                            type="button"
                                                            onClick={() => setPendingResourceFilter({ Status: "", Ram: "", Cpu: "" })}
                                                        >
                                                            Reset
                                                        </button>
                                                        <button
                                                            className="blue-btn"
                                                            type="button"
                                                            onClick={() => {
                                                                setFilterData(prev => ({ ...prev, Status: PendingResourceFilter.Status, Ram: PendingResourceFilter.Ram, Cpu: PendingResourceFilter.Cpu }));
                                                                setShowResourceFilterModal(false);
                                                            }}
                                                        >
                                                            Submit
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="date-nav-wrapper">
                                            <button
                                                className="date-nav-btn"
                                                title="Previous day"
                                                onClick={() => setSelectedDate(prev => {
                                                    const d = new Date(prev);
                                                    d.setDate(d.getDate() - 1);
                                                    return d;
                                                })}
                                            >
                                                <img src={LeftArrowImg} alt="Previous day" />
                                            </button>
                                            <DatePicker
                                                showIcon
                                                toggleCalendarOnIconClick
                                                selected={SelectedDate}
                                                onChange={handleChange}
                                                dateFormat="MMM dd, yyyy"
                                                popperProps={{ strategy: 'fixed' }}
                                                popperPlacement="bottom-start"
                                            />
                                            <button
                                                className="date-nav-btn"
                                                title="Next day"
                                                onClick={() => setSelectedDate(prev => {
                                                    const d = new Date(prev);
                                                    d.setDate(d.getDate() + 1);
                                                    return d;
                                                })}
                                            >
                                                <img src={RightArrowImg} alt="Next day" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="righttools">
                                        <div className="legend-wf">
                                            <span><div className="leg-bar running" ></div> Running</span>
                                            <span><div className="leg-bar completed" ></div> Completed</span>
                                            <span><div className="leg-bar scheduled" ></div> Scheduled</span>
                                            <span><div className="leg-bar failed" ></div> Partial Success / Failed</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="workflowtimeline-table">
                                    <div className="now-line" id="nowLine" style={{ left: "-100px", display: isSelectedDateToday ? "block" : "none" }} data-time={commonservices.convertTimeToLocal12Formate(new Date())}></div>
                                    <table id="workflowtimeline" data="">
                                        <thead>
                                            <tr>
                                                <th className=" sticky-workflow workflow-timeline-sticky text-nowrap zindex10 workflow">
                                                    Workflow

                                                </th>
                                                <th className=" sticky-workflow workflow-timeline-sticky text-nowrap zindex10 wfstatus">
                                                    WF. Status
                                                </th>
                                                <th className=" sticky-workflow workflow-timeline-sticky text-nowrap zindex10 wftime">
                                                    WF. Time
                                                </th>
                                                <th className=" sticky-workflow workflow-timeline-sticky text-nowrap zindex10 mstatus">
                                                    M. Status
                                                </th>
                                                <th className=" sticky-workflow workflow-timeline-sticky text-nowrap zindex10 mtime">
                                                    M. Time
                                                </th>
                                                {
                                                    slots.map((s, i) =>
                                                        <th className="timeslot workflow-timeline-sticky" id={s.replace(" ", "")}>
                                                            <div className="hour-label">{s}</div>
                                                            <div className="minute-slots">
                                                                <div className="minute-slot">:00</div>
                                                                <div className="minute-slot">:05</div>
                                                                <div className="minute-slot">:10</div>
                                                                <div className="minute-slot">:15</div>
                                                                <div className="minute-slot">:20</div>
                                                                <div className="minute-slot">:25</div>
                                                                <div className="minute-slot">:30</div>
                                                                <div className="minute-slot">:35</div>
                                                                <div className="minute-slot">:40</div>
                                                                <div className="minute-slot">:45</div>
                                                                <div className="minute-slot">:50</div>
                                                                <div className="minute-slot">:55</div>
                                                            </div>
                                                        </th>
                                                    )
                                                }
                                            </tr>

                                        </thead>
                                        <tbody>

                                            {
                                                Loading ?
                                                    <>
                                                        {/* Skeleton rows: 5 sticky columns + timeslot columns with fake time-blocks */}
                                                        {[
                                                            [[2, 90], [9, 150], [18, 75]],
                                                            [[4, 120], [12, 80], [20, 100]],
                                                            [[1, 60], [7, 130], [16, 90]],
                                                            [[5, 100], [11, 70], [22, 110]],
                                                            [[3, 80], [10, 120], [17, 60]],
                                                            [[6, 110], [14, 90], [21, 80]],
                                                            [[2, 90], [9, 150], [18, 75]],
                                                            [[4, 120], [12, 80], [20, 100]],
                                                            [[1, 60], [7, 130], [16, 90]],
                                                            [[5, 100], [11, 70], [22, 110]],
                                                            [[3, 80], [10, 120], [17, 60]],
                                                            [[6, 110], [14, 90], [21, 80]],
                                                        ].map((blocks, i) => (
                                                            <tr key={i}>
                                                                <td className="sticky-workflow workflow-timeline-sticky text-nowrap zindex10 workflow">
                                                                    <div className="wf-cell">
                                                                        <Skeleton width={120} height={13} style={{ marginBottom: 4 }} />
                                                                        <Skeleton width={60} height={10} borderRadius={50} />
                                                                    </div>
                                                                </td>
                                                                <td className="sticky-workflow workflow-timeline-sticky text-nowrap zindex10 wfstatus">
                                                                    <Skeleton width={80} height={20} borderRadius={999} />
                                                                </td>
                                                                <td className="sticky-workflow workflow-timeline-sticky text-nowrap zindex10 wftime">
                                                                    <Skeleton width={55} height={13} />
                                                                </td>
                                                                <td className="sticky-workflow workflow-timeline-sticky text-nowrap zindex10 mstatus">
                                                                    <Skeleton width={90} height={13} style={{ marginBottom: 4 }} />
                                                                    <Skeleton width={40} height={10} />
                                                                </td>
                                                                <td className="sticky-workflow workflow-timeline-sticky text-nowrap zindex10 mtime">
                                                                    <Skeleton width={55} height={13} />
                                                                </td>
                                                                {slots.map((s, si) => {
                                                                    const block = blocks.find(b => b[0] === si);
                                                                    return (
                                                                        <td key={si} className="timeslot">
                                                                            <div className="minute-slots" style={{ position: 'relative' }}>
                                                                                <div className="minute-slot"></div>
                                                                                <div className="minute-slot"></div>
                                                                                <div className="minute-slot"></div>
                                                                                <div className="minute-slot"></div>
                                                                                <div className="minute-slot"></div>
                                                                                <div className="minute-slot"></div>
                                                                                <div className="minute-slot"></div>
                                                                                <div className="minute-slot"></div>
                                                                                <div className="minute-slot"></div>
                                                                                <div className="minute-slot"></div>
                                                                                <div className="minute-slot"></div>
                                                                                <div className="minute-slot"></div>
                                                                                {block && (
                                                                                    <Skeleton
                                                                                        style={{ position: 'absolute', top: '11px', left: '4px', zIndex: 2 }}
                                                                                        width={block[1]}
                                                                                        height={22}
                                                                                        borderRadius={6}
                                                                                    />
                                                                                )}
                                                                            </div>
                                                                        </td>
                                                                    );
                                                                })}
                                                            </tr>
                                                        ))}
                                                    </>
                                                    :
                                                    <>
                                                        {
                                                            WorkflowList.map((wsGroup, wsIdx) => {
                                                                const isWsExpanded = expandedWorkspaces[wsGroup.id];

                                                                // Count running workflows in this workspace
                                                                const runningCount = wsGroup.workflows.filter(wf => wf.workflowstatus.toUpperCase() === "START").length;
                                                                const failedCount = wsGroup.workflows.filter((wf) =>
                                                                    Array.isArray(wf.slotdata)
                                                                    && wf.slotdata.some((slot) => ((slot.status || "").toUpperCase() === "PARTIAL SUCCESS"))
                                                                ).length;
                                                                const completedCount = wsGroup.workflows.filter(wf => wf.workflowstatus.toUpperCase() === "END").length;

                                                                return <React.Fragment key={wsGroup.id}>
                                                                    {/* Workspace Group Row */}
                                                                    <tr className={`workspace-group-row ${isWsExpanded ? "workspace-row-expanded" : ""}`}>
                                                                        <td
                                                                            className="sticky-workflow workflow-timeline-sticky text-nowrap zindex10 workflow"
                                                                            style={{ cursor: "pointer" }}
                                                                            onClick={() => toggleWorkspaceExpand(wsGroup.id)}
                                                                        >
                                                                            <div className="wf-cell workspace-wf-cell">
                                                                                <span className="workspace-count-circle workflow-count" title={`${wsGroup.workflows.length} workflow${wsGroup.workflows.length !== 1 ? "s" : ""}`}>{wsGroup.workflows.length}</span>
                                                                                <span className="wf-name workspace-name-ellipsis" title={wsGroup.workspacename === "Running" ? wsGroup.workspacename : `${wsGroup.workspacename} - ${wsGroup.clientname || ""}`}>{wsGroup.workspacename === "Running" ? wsGroup.workspacename : `${wsGroup.workspacename}${wsGroup.clientname ? ` - ${wsGroup.clientname}` : ""}`}</span>
                                                                            </div>
                                                                        </td>
                                                                        <td className="sticky-workflow workflow-timeline-sticky text-nowrap zindex10 wfstatus">
                                                                            <div className="d-flex align-items-center gap-1 flex-wrap workspace-status-circles">
                                                                                {runningCount > 0 && <span className="workspace-count-circle running" title={`${runningCount} running`}>{runningCount}</span>}
                                                                                {failedCount > 0 && <span className="workspace-count-circle failed" title={`${failedCount} failed`}>{failedCount}</span>}
                                                                                {completedCount > 0 && <span className="workspace-count-circle completed" title={`${completedCount} completed`}>{completedCount}</span>}
                                                                            </div>
                                                                        </td>
                                                                        <td className="sticky-workflow workflow-timeline-sticky text-nowrap zindex10 wftime">
                                                                            {wsGroup.totaltime}
                                                                        </td>
                                                                        <td className="sticky-workflow workflow-timeline-sticky text-nowrap zindex10 mstatus">
                                                                        </td>
                                                                        <td className="sticky-workflow workflow-timeline-sticky text-nowrap zindex10 mtime">
                                                                        </td>
                                                                        {
                                                                            slots.map((s, i) =>
                                                                                <td className="timeslot" key={i}>
                                                                                    <div className="minute-slots">
                                                                                        <div className="minute-slot"></div>
                                                                                        <div className="minute-slot"></div>
                                                                                        <div className="minute-slot"></div>
                                                                                        <div className="minute-slot"></div>
                                                                                        <div className="minute-slot"></div>
                                                                                        <div className="minute-slot"></div>
                                                                                        <div className="minute-slot"></div>
                                                                                        <div className="minute-slot"></div>
                                                                                        <div className="minute-slot"></div>
                                                                                        <div className="minute-slot"></div>
                                                                                        <div className="minute-slot"></div>
                                                                                        <div className="minute-slot"></div>
                                                                                    </div>
                                                                                </td>
                                                                            )
                                                                        }
                                                                    </tr>

                                                                    {/* Workflow Rows - Expanded under workspace */}
                                                                    {isWsExpanded && wsGroup.workflows.map((workflow, w) => {
                                                                        const isExpanded = expandedWorkflows[workflow.id];
                                                                        const machineStatusLabel = workflow.machinestatus
                                                                            ? `${workflow.machinestatus}(${workflow.noofmachinerun}/${workflow.noofmachine})`
                                                                            : `${workflow.noofmachinerun}/${workflow.noofmachine}`;

                                                                        // Collect all machine logs from ALL slotdata entries
                                                                        const machineLogs = workflow.slotdata?.flatMap(slot => slot.machinelog || []) || [];

                                                                        // Group machine logs by machine name
                                                                        const groupedMachines = machineLogs.reduce((acc, log) => {
                                                                            const key = log.machinename;
                                                                            if (!acc[key]) {
                                                                                acc[key] = {
                                                                                    machinename: key,
                                                                                    records: [],
                                                                                    totalSeconds: 0
                                                                                };
                                                                            }
                                                                            acc[key].records.push(log);
                                                                            if (log.starton && log.endon) {
                                                                                acc[key].totalSeconds += commonservices.GetSecondCounts(log.starton, log.endon);
                                                                            }
                                                                            return acc;
                                                                        }, {});

                                                                        // Convert to array, sort records by time, and get last status
                                                                        const groupedMachineList = Object.values(groupedMachines).map(machine => {
                                                                            // Sort records by starton time (chronological order)
                                                                            const sortedRecords = [...machine.records].sort((a, b) =>
                                                                                new Date(a.starton || 0) - new Date(b.starton || 0)
                                                                            );
                                                                            const lastRecord = sortedRecords[sortedRecords.length - 1];
                                                                            const lastRecordSeconds = lastRecord?.starton && lastRecord?.endon
                                                                                ? commonservices.GetSecondCounts(lastRecord.starton, lastRecord.endon)
                                                                                : 0;
                                                                            return {
                                                                                ...machine,
                                                                                records: sortedRecords,
                                                                                lastStatus: lastRecord?.status || "",
                                                                                lastRecordSeconds: lastRecordSeconds
                                                                            };
                                                                        });

                                                                        const wfBounds = getWorkflowBoundsPx(workflow.slotdata);
                                                                        const wfMinLeft = wfBounds.minLeft;
                                                                        const wfMaxRight = wfBounds.maxRight;

                                                                        return <React.Fragment key={workflow.id}>
                                                                            <tr id={workflow.workflowid} className={isExpanded ? "workflow-row-expanded" : ""}>
                                                                                <td
                                                                                    className=" sticky-workflow workflow-timeline-sticky text-nowrap zindex10 workflow"
                                                                                    style={{ cursor: machineLogs.length > 0 ? "pointer" : "default", paddingLeft: "20px" }}
                                                                                    onClick={() => machineLogs.length > 0 && toggleWorkflowExpand(workflow.id)}
                                                                                >
                                                                                    <div className="wf-cell">
                                                                                        <div className="workflow-name-line">
                                                                                            <img
                                                                                                src={editAction}
                                                                                                alt="editAction"
                                                                                                title="Edit workflow"
                                                                                                className="workflow-edit-icon"
                                                                                                onClick={(e) => openWorkflowEditPage(workflow.workflowid, e)}
                                                                                            />
                                                                                            <span className={`wf-name ${machineLogs.length > 0 ? "expandable" : ""}`}>{workflow.workflowname}</span>
                                                                                        </div>
                                                                                    </div>
                                                                                </td>
                                                                                <td className=" sticky-workflow workflow-timeline-sticky text-nowrap zindex10 wfstatus">
                                                                                    {commonservices.getWorkflowTimeLineStatus(workflow.workflowstatus)}
                                                                                </td>
                                                                                <td className=" sticky-workflow workflow-timeline-sticky text-nowrap zindex10 wftime">
                                                                                    {workflow.workflowtime ? workflow.workflowtime :
                                                                                        workflow.workflowstatus.toLowerCase() === "start" ?
                                                                                            <RunningTimer starton={workflow.workflowstarton} /> : ""}
                                                                                </td>
                                                                                <td className=" sticky-workflow workflow-timeline-sticky text-nowrap zindex10 mstatus">
                                                                                    <div className="d-flex align-items-center justify-content-between">
                                                                                        <div className={`machine-info ${workflow.workflowstatus.toLowerCase().replace(" ", "")}`}>
                                                                                            <span className="machine-name">{machineStatusLabel}</span>
                                                                                        </div>
                                                                                        {workflow.slotdata?.[0]?.workflowulogid && (
                                                                                            <img
                                                                                                src={charticon}
                                                                                                alt="chart"
                                                                                                title="Usage chart"
                                                                                                className="cursor-pointer"
                                                                                                style={{ width: "16px", marginLeft: "8px" }}
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    openChartModal(
                                                                                                        workflow.slotdata?.[0]?.workflowulogid,
                                                                                                        "",
                                                                                                        `${workflow.workflowname} Usage`
                                                                                                    );
                                                                                                }}
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                </td>
                                                                                <td className=" sticky-workflow workflow-timeline-sticky text-nowrap zindex10 mtime">
                                                                                    {workflow.machinetime}
                                                                                </td>
                                                                                {
                                                                                    slots.map((s, i) =>
                                                                                        <td className="timeslot" key={i}>
                                                                                            <div className="minute-slots">
                                                                                                <div className="minute-slot"></div>
                                                                                                <div className="minute-slot"></div>
                                                                                                <div className="minute-slot"></div>
                                                                                                <div className="minute-slot"></div>
                                                                                                <div className="minute-slot"></div>
                                                                                                <div className="minute-slot"></div>
                                                                                                <div className="minute-slot"></div>
                                                                                                <div className="minute-slot"></div>
                                                                                                <div className="minute-slot"></div>
                                                                                                <div className="minute-slot"></div>
                                                                                                <div className="minute-slot"></div>
                                                                                                <div className="minute-slot"></div>
                                                                                                {
                                                                                                    i === 0 &&
                                                                                                    workflow.slotdata.map((slot, slotIdx) => {
                                                                                                        const statusLower = slot.status.toLowerCase();

                                                                                                        // In synthetic Running workspace, do not show scheduled (gray) bars.
                                                                                                        if (wsGroup.workspacename === "Running" && statusLower === "schedule") return null;

                                                                                                        if (!isRecordOnSelectedDate(slot.starton || slot.endon)) return null;

                                                                                                        // Running if status is "start" OR if workflow hasn't ended (no valid endon)
                                                                                                        const isRunning = statusLower === "start" || (!slot.endon && statusLower !== "end" && statusLower !== "schedule" && statusLower !== "partial success");
                                                                                                        const nowForPx = new Date();
                                                                                                        const nowPx = ((nowForPx.getHours() * 60) + nowForPx.getMinutes()) * PIXELS_PER_MINUTE;
                                                                                                        const slotStartPxRaw = getPxFromDate(slot.starton);
                                                                                                        const slotStartPx = isRunning ? Math.min(slotStartPxRaw, nowPx) : slotStartPxRaw;

                                                                                                        // Use RunningTimeBlock for running workflows (grows with time)
                                                                                                        if (isRunning && slot.starton) {
                                                                                                            return <RunningTimeBlock
                                                                                                                key={slotIdx}
                                                                                                                starton={slot.starton}
                                                                                                                leftPx={slotStartPx}
                                                                                                                workflowname={workflow.workflowname}
                                                                                                                onClick={() => fnWorkflowMachineLoggetlist(slot.workflowulogid)}
                                                                                                            />;
                                                                                                        }

                                                                                                        // Static time block for completed/scheduled workflows
                                                                                                        let wfBar;
                                                                                                        if (slot.endon) {
                                                                                                            const slotEndPx = getPxFromDate(slot.endon);
                                                                                                            wfBar = getBarStyle({
                                                                                                                startPx: slotStartPx,
                                                                                                                endPx: slotEndPx,
                                                                                                                fallbackWidthPx: (slot.minutes || 1) * PIXELS_PER_MINUTE,
                                                                                                            });
                                                                                                        } else {
                                                                                                            wfBar = getBarStyle({
                                                                                                                startPx: slotStartPx,
                                                                                                                endPx: slotStartPx,
                                                                                                                fallbackWidthPx: (slot.minutes || 1) * PIXELS_PER_MINUTE,
                                                                                                            });
                                                                                                        }
                                                                                                        return <div key={slotIdx} minute={slot.minutes || 1} style={{ width: `${wfBar.width}px`, left: `${wfBar.left}px` }} className={`time-block ${statusLower.replace(" ", "")}`} title={`${workflow.workflowname} - ${formatTimelineTime(slot.starton)} to ${formatTimelineTime(slot.endon)}`} onClick={() => {
                                                                                                            fnWorkflowMachineLoggetlist(
                                                                                                                slot.workflowulogid
                                                                                                            );
                                                                                                        }}>
                                                                                                            <div className="details">{workflow.workflowname}</div>
                                                                                                        </div>
                                                                                                    })
                                                                                                }
                                                                                            </div>

                                                                                        </td>
                                                                                    )
                                                                                }
                                                                            </tr>

                                                                            {/* Machine Log Rows - Expanded */}
                                                                            {isExpanded && groupedMachineList.map((machine, mIdx) => (
                                                                                <tr key={`${workflow.id}-machine-${mIdx}`} className="machine-log-row">
                                                                                    <td className="sticky-workflow workflow-timeline-sticky text-nowrap zindex10 workflow machine-log-cell">
                                                                                        <div className="wf-cell" style={{ paddingLeft: "50px" }}>
                                                                                            <span className="machine-icon" style={{ color: "#6b7280" }}>⚙</span>
                                                                                            <span className="wf-name" style={{ fontSize: "12px", color: "#374151" }}>{machine.machinename}</span>
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className="sticky-workflow workflow-timeline-sticky text-nowrap zindex10 wfstatus machine-log-cell">
                                                                                        {machine.records[0]?.ram || ""}
                                                                                    </td>
                                                                                    <td className="sticky-workflow workflow-timeline-sticky text-nowrap zindex10 wftime machine-log-cell">
                                                                                        {machine.records[0]?.cpu ? `${machine.records[0].cpu} cores` : ""}
                                                                                    </td>
                                                                                    <td className="sticky-workflow workflow-timeline-sticky text-nowrap zindex10 mstatus machine-log-cell">
                                                                                        <div className="d-flex align-items-center justify-content-between">
                                                                                            <div>{commonservices.getWorkflowTimeLineStatus(machine.lastStatus)}</div>
                                                                                            <div>{machine.records?.[0]?.workflowulogid && (
                                                                                                <img
                                                                                                    src={charticon}
                                                                                                    alt="chart"
                                                                                                    title="Usage chart"
                                                                                                    className="cursor-pointer"
                                                                                                    style={{ width: "14px", marginLeft: "8px" }}
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        openChartModal(
                                                                                                            machine.records?.[0]?.workflowulogid,
                                                                                                            machine.records?.[0]?.machineid,
                                                                                                            `${machine.machinename} Usage`
                                                                                                        );
                                                                                                    }}
                                                                                                />
                                                                                            )}</div>
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className="sticky-workflow workflow-timeline-sticky text-nowrap zindex10 mtime machine-log-cell">
                                                                                        {machine.lastRecordSeconds > 0 ? commonservices.ConvetSecondInTimeFormate(machine.lastRecordSeconds) : ""}
                                                                                    </td>
                                                                                    {
                                                                                        slots.map((s, i) =>
                                                                                            <td className="timeslot machine-log-cell" key={i}>
                                                                                                <div className="minute-slots" style={{ backgroundColor: "rgba(243, 244, 246, 0.5)" }}>
                                                                                                    <div className="minute-slot"></div>
                                                                                                    <div className="minute-slot"></div>
                                                                                                    <div className="minute-slot"></div>
                                                                                                    <div className="minute-slot"></div>
                                                                                                    <div className="minute-slot"></div>
                                                                                                    <div className="minute-slot"></div>
                                                                                                    <div className="minute-slot"></div>
                                                                                                    <div className="minute-slot"></div>
                                                                                                    <div className="minute-slot"></div>
                                                                                                    <div className="minute-slot"></div>
                                                                                                    <div className="minute-slot"></div>
                                                                                                    <div className="minute-slot"></div>
                                                                                                    {
                                                                                                        i === 0 && machine.records.map((record, rIdx) => {
                                                                                                            // Show time block if starton exists (even without endon for failed/running)
                                                                                                            if (!record.starton || !isRecordOnSelectedDate(record.starton || record.endon)) return null;

                                                                                                            const nowForPx = new Date();
                                                                                                            const nowPx = ((nowForPx.getHours() * 60) + nowForPx.getMinutes()) * PIXELS_PER_MINUTE;
                                                                                                            const recordStatusLower = (record.status || "").toLowerCase();
                                                                                                            const recordStartPxRaw = getPxFromDate(record.starton);
                                                                                                            const recordStartPx = recordStatusLower === "start"
                                                                                                                ? Math.min(recordStartPxRaw, nowPx)
                                                                                                                : recordStartPxRaw;
                                                                                                            let machineBar;
                                                                                                            if (record.endon) {
                                                                                                                const recordEndPx = getPxFromDate(record.endon);
                                                                                                                const tinyEdge = wfMaxRight && recordEndPx >= wfMaxRight
                                                                                                                    ? "end"
                                                                                                                    : (recordStartPx <= wfMinLeft ? "start" : "none");
                                                                                                                machineBar = getBarStyle({
                                                                                                                    startPx: recordStartPx,
                                                                                                                    endPx: recordEndPx,
                                                                                                                    fallbackWidthPx: MIN_VISIBLE_BAR_PX,
                                                                                                                    minLeft: wfMinLeft,
                                                                                                                    maxRight: wfMaxRight,
                                                                                                                    tinyEdge,
                                                                                                                });
                                                                                                            } else {
                                                                                                                const duration = commonservices.GetSecondCounts(record.starton, new Date().toISOString());
                                                                                                                const machineNowPx = ((new Date().getHours() * 60) + new Date().getMinutes()) * PIXELS_PER_MINUTE;
                                                                                                                machineBar = getBarStyle({
                                                                                                                    startPx: recordStartPx,
                                                                                                                    endPx: recordStartPx,
                                                                                                                    fallbackWidthPx: Math.max(duration / 60, 1) * PIXELS_PER_MINUTE,
                                                                                                                    minLeft: wfMinLeft,
                                                                                                                    maxRight: wfMaxRight !== null ? Math.min(wfMaxRight, machineNowPx) : machineNowPx,
                                                                                                                    tinyEdge: recordStartPx <= wfMinLeft ? "start" : "none",
                                                                                                                });
                                                                                                            }

                                                                                                            return (
                                                                                                                <div
                                                                                                                    key={rIdx}
                                                                                                                    style={{
                                                                                                                        width: `${machineBar.width}px`,
                                                                                                                        left: `${machineBar.left}px`,
                                                                                                                        height: "16px",
                                                                                                                        top: "14px"
                                                                                                                    }}
                                                                                                                    className={`time-block ${record.status.toLowerCase().replace(" ", "")}`}
                                                                                                                    title={`${record.machinename} - ${formatTimelineTime(record.starton)} to ${record.endon ? formatTimelineTime(record.endon) : 'In Progress'}`}
                                                                                                                >
                                                                                                                    <div className="details" style={{ fontSize: "9px" }}>{record.machinename}</div>
                                                                                                                </div>
                                                                                                            );
                                                                                                        })
                                                                                                    }
                                                                                                </div>
                                                                                            </td>
                                                                                        )
                                                                                    }
                                                                                </tr>
                                                                            ))}
                                                                        </React.Fragment>
                                                                    })}
                                                                </React.Fragment>
                                                            })
                                                        }

                                                    </>
                                            }


                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        </div>
                    </Container>
                </div>
            </div>


            {modalDetailShow && workflowulogid && (
                <WorkFlowMachineLogDetailModal
                    show={modalDetailShow}
                    workflowulogid={workflowulogid}
                    onHide={() => setDetailModalShow(false)}
                />
            )}

            <Modal
                show={chartModalShow}
                onHide={() => setChartModalShow(false)}
                size="lg"
                centered
                className="normal-modal workflow-timeline-chart-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>{chartTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* Skeleton overlay - shown until all charts are ready */}
                    {chartLoading && (
                        <div>
                            {["CPU Usage", "Memory Usage", "Network Usage"].map((label, idx) => (
                                <div key={label} className={idx < 2 ? "mb-3" : ""}>
                                    <div className="dashboard-stacked-chart flow-common-white-bg mb-2">
                                        <Skeleton width={100} height={14} />
                                    </div>
                                    <div style={{ height: "120px" }}>
                                        <Skeleton height={120} borderRadius={4} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {/* Charts render hidden behind skeleton, visible once ready */}
                    {chartData ? (
                        <div style={{ visibility: chartLoading ? "hidden" : "visible", height: chartLoading ? 0 : "auto", overflow: chartLoading ? "hidden" : "visible" }}>
                            <div className="dashboard-stacked-chart flow-common-white-bg mb-2">
                                <h4 style={{ fontSize: "14px", marginBottom: 0 }}>CPU Usage</h4>
                            </div>
                            <div className="mb-3" style={{ height: "120px" }}>
                                <WorkFlowTimeLineChart data={chartData} params="CPU Usage" tickFontSize={8} onReady={handleChartReady} />
                            </div>

                            <div className="dashboard-stacked-chart flow-common-white-bg mb-2">
                                <h4 style={{ fontSize: "14px", marginBottom: 0 }}>Memory Usage</h4>
                            </div>
                            <div className="mb-3" style={{ height: "120px" }}>
                                <WorkFlowTimeLineChart data={chartData} params="Memory Usage" tickFontSize={8} onReady={handleChartReady} />
                            </div>

                            <div className="dashboard-stacked-chart flow-common-white-bg mb-2">
                                <h4 style={{ fontSize: "14px", marginBottom: 0 }}>Network Usage</h4>
                            </div>
                            <div style={{ height: "120px" }}>
                                <WorkFlowTimeLineChart data={chartData} params="Network Usage" tickFontSize={8} onReady={handleChartReady} />
                            </div>
                        </div>
                    ) : (
                        !chartLoading && <Nodata />
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
}

export default WorkFlowTimeLine;
